import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Chip, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline'
import {
  aujourdhuiBoutique,
  format,
  money,
  PAYMENT_METHOD_LABELS,
  replier,
  variation,
  type CurrencyCode,
  type Pas,
  type PointVentes,
} from '@iobiz/shared'
import {
  useCoutsArticlesAbsents,
  useItems,
  useDepensesParCategorie,
  useSortiesSerie,
  useStockLevels,
  useTableauDeBord,
} from '../data/hooks.js'
import { useSessionStore } from '../stores/sessionStore.js'
import { etatDeStock } from '../data/repositories/catalog.local.js'
import { GrapheDialog, type Indicateur } from '../features/dashboard/GrapheDialog.js'
import { CarteIndicateur, CarteTableau, LienVoir } from '../features/dashboard/briques.js'
import { CarteVedette } from '../features/dashboard/CarteVedette.js'
import { compact, libellesDe } from '../features/dashboard/tranches.js'
import type { ArticleEnAlerte } from '../ui/charts/GrapheStock.js'
import { GrapheAire } from '../ui/charts/GrapheAire.js'
import { GrapheAnneau } from '../ui/charts/GrapheAnneau.js'
import { couleursGraphe, GrapheColonnes } from '../ui/charts/GrapheColonnes.js'
import { Sparkline } from '../ui/charts/Sparkline.js'
import { palette } from '../ui/theme/tokens.js'

/**
 * Tableau de bord (W-07).
 *
 * ⭐ UNE commande en tête — Jour, Mois, Année — et toute la page suit : la
 * carte vedette, la courbe, les répartitions, les palmarès. Des horizons
 * différents d'une carte à l'autre faisaient comparer le chiffre du jour à
 * la catégorie de l'année sans qu'on s'en aperçoive.
 *
 * ⭐ Tout vient de la RÉPLIQUE, lue en une passe — sauf les dépenses, qui ne
 * sont pas répliquées : hors ligne, ce qui en dépend affiche un tiret plutôt
 * qu'un chiffre faux.
 *
 * ⚠️ « Aujourd'hui » est celui de la BOUTIQUE, pas du poste : consulté de
 * Paris le soir, un tableau de Bamako ne doit pas basculer au lendemain.
 */

const CLE_PAS = 'iobiz.tableau.pas'

const PERIODES: Record<Pas, { onglet: string; vedette: string; comparaison?: string }> = {
  jour: { onglet: 'Jour', vedette: 'Aujourd’hui' },
  mois: { onglet: 'Mois', vedette: 'Ce mois-ci', comparaison: 'vs mois dernier, à date' },
  annee: { onglet: 'Année', vedette: 'Cette année', comparaison: 'vs l’an dernier, à date' },
}

/** La portée d'un total de courbe, en quelques mots — pour les entêtes. */
const PORTEES: Record<Pas, string> = { jour: '30 jours', mois: '12 mois', annee: 'toutes années' }

/** L'étendue des courbes, selon leur pas. */
const HORIZONS: Record<Pas, string> = {
  jour: '30 derniers jours',
  mois: '12 derniers mois',
  annee: 'Par année',
}

function lirePas(): Pas {
  try {
    const v = localStorage.getItem(CLE_PAS)
    return v === 'jour' || v === 'mois' || v === 'annee' ? v : 'mois'
  } catch {
    return 'mois'
  }
}

export function DashboardPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => s.stores.find((b) => b.slug === slug))
  const storeId = store?.storeId
  const devise = (store?.currencyCode ?? 'XAF') as CurrencyCode
  const fuseau = store?.timezone ?? 'Africa/Douala'
  const aujourdhui = aujourdhuiBoutique(fuseau)

  // ⭐ L'horizon choisi est RETENU : on ne rouvre pas le tableau sur « Mois »
  // quand on travaille à la journée.
  const [pas, setPasEtat] = useState<Pas>(lirePas)
  const setPas = (p: Pas): void => {
    setPasEtat(p)
    try {
      localStorage.setItem(CLE_PAS, p)
    } catch {
      // Sans stockage, l'horizon ne sera simplement pas retenu.
    }
  }

  // Le prix d'achat des articles supprimés depuis leur vente, sans lequel le
  // bénéfice les compterait pour zéro.
  const coutsAbsents = useCoutsArticlesAbsents(storeId)
  const tb = useTableauDeBord(storeId, pas, aujourdhui, fuseau, coutsAbsents)
  // Les courbes suivent le pas des TENDANCES, qui peut différer de la période.
  const pasTendance = tb?.pasTendance ?? pas
  const { sorties } = useSortiesSerie(storeId, tb?.tranches[0]?.du, aujourdhui, pasTendance)
  const articles = useItems(storeId, { limit: 200, status: 'active' })
  const stocks = useStockLevels(storeId)

  const [graphe, setGraphe] = useState<{ indicateur: Indicateur; pas: Pas } | null>(null)
  const voir = (indicateur: Indicateur) => () => setGraphe({ indicateur, pas })
  const aller = (chemin: string) => () => navigate(`/s/${slug}/${chemin}`)

  const prix = (v: number): string => format(money(v, devise), 'fr-FR')
  const nombre = (v: number): string => new Intl.NumberFormat('fr-FR').format(v)
  const periode = PERIODES[pas]
  const sombre = useTheme().palette.mode === 'dark'
  // ⚠️ Dit POURQUOI les courbes sont au mois en « Année » : sans cela, on croit
  // le sélecteur resté sur « Mois ».
  const horizon =
    pasTendance !== pas
      ? `${HORIZONS[pasTendance]} · une seule année d’activité`
      : HORIZONS[pasTendance]

  const points: PointVentes[] = tb?.points ?? []
  const actuel = tb?.actuel
  const { courts, longs } = libellesDe(tb?.tranches ?? [], pasTendance)

  // Les sorties par tranche. Une tranche absente de la réponse n'a rien
  // dépensé ; une réponse absente, elle, veut dire « on ne sait pas ».
  const sortiesParCle = new Map(sorties?.map((s) => [s.cle, s]) ?? [])
  const courantesDe = (cle: string): number => sortiesParCle.get(cle)?.courantes ?? 0

  /**
   * ⭐ Bénéfice net = ventes HT − prix d'achat des articles vendus − dépenses
   * courantes. Les achats de marchandise n'y entrent pas : ils sont comptés
   * article par article, au moment où la marchandise se vend.
   */
  const beneficeDe = (p: PointVentes): number | null =>
    sorties ? p.ventesHt - p.coutAchat - courantesDe(p.cle) : null
  // Les dépenses courantes de la PÉRIODE : la somme des tranches qu'elle
  // couvre — une seule en Jour ou en Mois, les mois de l'année en Année.
  const courante = tb?.courante
  const tranchesCourantes = courante
    ? (tb?.tranches ?? []).filter((t) => t.du >= courante.du && t.au <= courante.au)
    : []
  const courantesActuelles =
    sorties && courante
      ? tranchesCourantes.reduce((somme, t) => somme + courantesDe(t.cle), 0)
      : null
  const marchandiseActuelle =
    sorties && courante
      ? tranchesCourantes.reduce((somme, t) => somme + (sortiesParCle.get(t.cle)?.produits ?? 0), 0)
      : null
  const beneficeActuel =
    actuel && courantesActuelles !== null
      ? actuel.ventesHt - actuel.coutAchat - courantesActuelles
      : null

  /** Mène aux produits à compléter : c'est leur fiche qui corrige le chiffre. */
  const completerPrixAchat = (): void => {
    setGraphe(null)
    navigate(`/s/${slug}/catalogue?prixAchat=manquant`)
  }

  /**
   * Articles réellement sous leur seuil — la MÊME règle que le catalogue.
   * Recopiée ici, elle avait déjà divergé : la carte annonçait un nombre que
   * la liste, ouverte d'un clic, ne confirmait pas.
   */
  const articlesEnAlerte: ArticleEnAlerte[] = (articles?.data ?? []).flatMap((a) => {
    const stock = stocks.get(a.id) ?? 0
    const etat = etatDeStock(a, stock)
    return etat === 'rupture' || etat === 'alerte'
      ? [{ id: a.id, nom: a.name, stock, seuil: a.minStockLevel, etat }]
      : []
  })

  // Les TOTAUX de ce que dessine la courbe — pour son entête.
  const caSurHorizon = points.reduce((somme, p) => somme + p.ca, 0)
  const beneficeSurHorizon = sorties
    ? points.reduce((somme, p) => somme + (beneficeDe(p) ?? 0), 0)
    : null
  const [couleurA, couleurB] = couleursGraphe(sombre)
  const portee = PORTEES[pasTendance]

  const avant = tb?.avant ?? null
  const caTotalPeriode = actuel?.ca ?? 0

  // Répartitions de la période en cours.
  const categories = replier(tb?.categories ?? [], 5)

  /**
   * ⭐ Les dépenses courantes par catégorie, sur LA MÊME période que le
   * chiffre d'affaires — le sélecteur Jour / Mois / Année les pilote toutes
   * les deux. Deux horizons différents sur un même écran feraient comparer le
   * loyer du mois aux ventes du jour.
   */
  const depensesBrutes = useDepensesParCategorie(storeId, tb?.courante.du, tb?.courante.au)
  const depensesParCat = replier(
    (depensesBrutes ?? []).map((d) => ({
      cle: d.categoryId ?? 'sans',
      // Les dépenses SANS catégorie ne disparaissent pas du total : les taire
      // ferait un anneau dont les parts n'additionnent pas le total affiché.
      libelle: d.categoryName ?? 'Sans catégorie',
      montant: d.total,
    })),
    5,
  )
  const depensesCourantesTotal = (depensesBrutes ?? []).reduce((s, d) => s + d.total, 0)
  const encaissements = (tb?.encaissements ?? []).map((e) => ({
    cle: e.moyen,
    libelle: PAYMENT_METHOD_LABELS[e.moyen],
    montant: e.montant,
  }))

  const dateLongue = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${aujourdhui}T12:00:00Z`))

  return (
    <Box>
      {/* ─── Entête : le titre, et LA commande qui règle toute la page. */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 3,
          mb: 5,
        }}
      >
        <Box>
          <Typography variant="h1">Tableau de bord</Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              mt: 0.5,
              '&::first-letter': { textTransform: 'uppercase' },
            }}
          >
            {store?.name ? `${store.name} · ` : ''}
            {dateLongue}
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={pas}
          onChange={(_, v: Pas | null) => v && setPas(v)}
          aria-label="Période du tableau de bord"
          sx={{
            bgcolor: 'background.paper',
            '& .MuiToggleButton-root': { px: 3, textTransform: 'none', fontWeight: 600 },
          }}
        >
          {(['jour', 'mois', 'annee'] as const).map((p) => (
            <ToggleButton key={p} value={p}>
              {PERIODES[p].onglet}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 3, md: 4 },
          gridTemplateColumns: { xs: '1fr', md: 'repeat(12, 1fr)' },
          // Pendant un recalcul, la page garde son dessin, estompée — ni
          // squelette, ni saut de mise en page.
          opacity: tb ? 1 : 0.6,
          transition: 'opacity 150ms',
        }}
      >
        {/* ─── 1. La vedette, puis la courbe. */}
        <Box sx={{ gridColumn: { md: 'span 4' }, display: 'flex', flexDirection: 'column' }}>
          <CarteVedette
            periode={periode.vedette}
            ca={prix(caTotalPeriode)}
            variation={avant ? variation(caTotalPeriode, avant.ca) : null}
            {...(periode.comparaison ? { libelleVariation: periode.comparaison } : {})}
            benefice={beneficeActuel === null ? '—' : prix(beneficeActuel)}
            beneficeNegatif={beneficeActuel !== null && beneficeActuel < 0}
            depenses={courantesActuelles === null ? '—' : prix(courantesActuelles)}
            tendance={points.map((p) => p.ca)}
            lignesSansPrixAchat={beneficeActuel !== null ? (actuel?.lignesSansPrixAchat ?? 0) : 0}
            onCompleterPrixAchat={completerPrixAchat}
            onClick={aller('ventes')}
            onGraphe={voir('ca')}
          />
        </Box>

        <CarteTableau
          titre="Évolution du chiffre d’affaires"
          sousTitre={horizon}
          chiffres={[
            {
              libelle: `Chiffre d’affaires · ${portee}`,
              valeur: prix(caSurHorizon),
              couleur: couleurA,
            },
            {
              libelle: `Bénéfice net · ${portee}`,
              valeur: beneficeSurHorizon === null ? '—' : prix(beneficeSurHorizon),
              couleur: couleurB,
            },
          ]}
          action={<LienVoir libelle="Voir les ventes" onClick={aller('ventes')} />}
          sx={{ gridColumn: { md: 'span 8' } }}
        >
          <GrapheAire
            titre={`Chiffre d'affaires et bénéfice net, ${horizon}`}
            libelles={courts}
            libellesLongs={longs}
            aire={{ nom: 'Chiffre d’affaires', valeurs: points.map((p) => p.ca) }}
            {...(sorties
              ? { ligne: { nom: 'Bénéfice net', valeurs: points.map((p) => beneficeDe(p)) } }
              : {})}
            formater={prix}
            formaterAxe={(v) => compact.format(v)}
            hauteur={236}
            sansLegende
          />
        </CarteTableau>

        {/* ─── 2. Les indicateurs, chacun avec sa tendance. */}
        <Box
          sx={{
            gridColumn: { md: 'span 12' },
            display: 'grid',
            gap: { xs: 3, md: 4 },
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          }}
        >
          <CarteIndicateur
            libelle="Ventes"
            valeur={nombre(actuel?.ventes ?? 0)}
            variation={avant ? variation(actuel?.ventes ?? 0, avant.ventes) : null}
            {...(periode.comparaison ? { libelleVariation: periode.comparaison } : {})}
            tendance={points.map((p) => p.ventes)}
            onClick={aller('ventes')}
            onGraphe={voir('ventes')}
          />
          <CarteIndicateur
            libelle="Panier moyen"
            valeur={actuel?.panierMoyen != null ? prix(actuel.panierMoyen) : '—'}
            variation={
              avant?.panierMoyen != null && actuel?.panierMoyen != null
                ? variation(actuel.panierMoyen, avant.panierMoyen)
                : null
            }
            {...(periode.comparaison ? { libelleVariation: periode.comparaison } : {})}
            tendance={points.map((p) => p.panierMoyen)}
            onClick={aller('ventes')}
            onGraphe={voir('panier')}
          />
          <CarteIndicateur
            libelle="Nouveaux clients"
            valeur={nombre(tb?.nouveauxClientsActuel ?? 0)}
            unite={`sur ${nombre(tb?.clientsTotal ?? 0)}`}
            variation={
              tb?.nouveauxClientsAvant != null
                ? variation(tb.nouveauxClientsActuel, tb.nouveauxClientsAvant)
                : null
            }
            {...(periode.comparaison ? { libelleVariation: periode.comparaison } : {})}
            tendance={tb?.nouveauxClients ?? []}
            onClick={aller('clients')}
            onGraphe={voir('clients')}
          />
          <CarteIndicateur
            libelle="Produits en alerte"
            valeur={nombre(articlesEnAlerte.length)}
            {...(articlesEnAlerte.length > 0
              ? { avertissement: 'À réapprovisionner' }
              : { note: 'Tout est au-dessus du seuil' })}
            onClick={aller('catalogue?stock=attention')}
            onGraphe={voir('alerte')}
          />
        </Box>

        {/* ─── 3. Les répartitions de la période. */}
        <CarteTableau
          titre="Ventes par catégorie"
          sousTitre={periode.vedette}
          chiffres={[
            {
              libelle: `Total · ${periode.vedette.toLowerCase()}`,
              valeur: prix(categories.reduce((somme, c) => somme + c.montant, 0)),
            },
          ]}
          action={<LienVoir libelle="Catalogue" onClick={aller('catalogue')} />}
          sx={{ gridColumn: { md: 'span 4' } }}
        >
          <GrapheAnneau
            titre="Ventes par catégorie"
            parts={categories}
            formater={(v) => compact.format(v)}
            libelleTotal="Total"
            taille={150}
          />
        </CarteTableau>

        {/* ⭐ Les dépenses COURANTES par catégorie, présentées comme les
            ventes : même anneau, même période, même lecture. C'est là qu'on
            voit un loyer s'envoler ou un poste de transport dériver — un
            total unique le noyait. */}
        <CarteTableau
          titre="Dépenses par catégorie"
          sousTitre={periode.vedette}
          chiffres={[
            {
              libelle: `Total · ${periode.vedette.toLowerCase()}`,
              valeur: depensesBrutes === undefined ? '—' : prix(depensesCourantesTotal),
            },
          ]}
          action={<LienVoir libelle="Dépenses" onClick={aller('depenses')} />}
          sx={{ gridColumn: { md: 'span 4' } }}
        >
          <GrapheAnneau
            titre="Dépenses par catégorie"
            parts={depensesParCat}
            formater={(v) => compact.format(v)}
            libelleTotal="Total"
            taille={150}
          />
        </CarteTableau>

        <CarteTableau
          titre="Dépenses"
          sousTitre={horizon}
          chiffres={[
            {
              libelle: `Marchandise · ${periode.vedette.toLowerCase()}`,
              valeur: marchandiseActuelle === null ? '—' : prix(marchandiseActuelle),
              couleur: couleurA,
              // ⚠️ Dit ce que le chiffre COMPTE : un produit créé sans quantité
              // n'a rien acheté, et l'on cherchait « Test prod » ici.
              aide: 'entrées en stock',
            },
            {
              libelle: `Courantes · ${periode.vedette.toLowerCase()}`,
              valeur: courantesActuelles === null ? '—' : prix(courantesActuelles),
              couleur: couleurB,
              aide: 'loyer, salaires…',
            },
          ]}
          action={<LienVoir libelle="Voir" onClick={aller('depenses')} />}
          sx={{ gridColumn: { md: 'span 4' } }}
        >
          {sorties ? (
            <>
              <GrapheColonnes
                titre={`Dépenses, ${horizon}`}
                sansLegende
                libelles={courts}
                libellesLongs={longs}
                colonnes={[
                  {
                    nom: 'Marchandise',
                    valeurs: (tb?.tranches ?? []).map(
                      (t) => sortiesParCle.get(t.cle)?.produits ?? 0,
                    ),
                  },
                  {
                    nom: 'Courantes',
                    valeurs: (tb?.tranches ?? []).map((t) => courantesDe(t.cle)),
                  },
                ]}
                formater={prix}
                formaterAxe={(v) => compact.format(v)}
              />
            </>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', py: 6, textAlign: 'center' }}
            >
              Les dépenses arrivent du serveur : indisponibles hors ligne.
            </Typography>
          )}
        </CarteTableau>

        <CarteTableau
          titre="Encaissements"
          sousTitre={`${periode.vedette}, par moyen de paiement`}
          chiffres={[
            {
              libelle: `Encaissé · ${periode.vedette.toLowerCase()}`,
              valeur: prix(encaissements.reduce((somme, e) => somme + e.montant, 0)),
            },
          ]}
          sx={{ gridColumn: { md: 'span 4' } }}
        >
          <GrapheAnneau
            titre="Encaissements par moyen de paiement"
            parts={encaissements}
            formater={(v) => compact.format(v)}
            libelleTotal="Encaissé"
            demi
            taille={200}
          />
        </CarteTableau>

        {/* ─── 4. Ce qui se vend, et ce qui manque. */}
        <CarteTableau
          titre="Meilleures ventes"
          sousTitre={`${periode.vedette} · tendance sur ${horizon.toLowerCase()}`}
          action={<LienVoir libelle="Catalogue" onClick={aller('catalogue')} />}
          sx={{ gridColumn: { md: 'span 8' } }}
        >
          {(tb?.meilleures ?? []).length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', py: 6, textAlign: 'center' }}
            >
              Aucune vente sur la période.
            </Typography>
          ) : (
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
              {(tb?.meilleures ?? []).map((a, i) => (
                <Box
                  component="li"
                  key={a.itemId}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '32px 1fr auto', sm: '32px 1fr 64px 110px 120px' },
                    alignItems: 'center',
                    gap: 3,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-of-type': { borderBottom: 0 },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 700,
                      fontSize: 13,
                      bgcolor: i === 0 ? palette.brand[50] : 'action.hover',
                      color: i === 0 ? palette.brand[700] : 'text.secondary',
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={a.libelle}>
                      {a.libelle}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {caTotalPeriode > 0
                        ? `${Math.round((a.montant / caTotalPeriode) * 100)} % du chiffre`
                        : ''}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      textAlign: 'right',
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    × {nombre(a.quantite)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right' }} noWrap>
                    {prix(a.montant)}
                  </Typography>
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Sparkline valeurs={a.serie} titre={`Tendance de ${a.libelle}`} hauteur={32} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CarteTableau>

        <CarteTableau
          titre="Produits en alerte"
          sousTitre="Sous leur seuil de stock"
          action={
            articlesEnAlerte.length > 0 ? (
              <LienVoir libelle="Tout voir" onClick={aller('catalogue?stock=attention')} />
            ) : undefined
          }
          sx={{ gridColumn: { md: 'span 4' } }}
        >
          {articlesEnAlerte.length === 0 ? (
            <Box
              sx={{ py: 5, display: 'grid', placeItems: 'center', gap: 1, color: 'text.secondary' }}
            >
              <CheckCircleIcon sx={{ color: palette.success[500] }} />
              <Typography variant="body2">Aucun produit sous son seuil.</Typography>
            </Box>
          ) : (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.5 }}>
              {[...articlesEnAlerte]
                .sort((a, b) => a.stock - b.stock)
                .slice(0, 6)
                .map((a) => (
                  <Box
                    component="li"
                    key={a.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto',
                      gap: 2,
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={a.nom}>
                        {a.nom}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {nombre(a.stock)} en stock · seuil {nombre(a.seuil)}
                      </Typography>
                    </Box>
                    {/* ⚠️ Un statut : couleur ET libellé, jamais la couleur seule. */}
                    <Chip
                      size="small"
                      label={a.etat === 'rupture' ? 'Rupture' : 'Alerte'}
                      sx={{
                        fontWeight: 700,
                        bgcolor: a.etat === 'rupture' ? palette.danger[50] : palette.warning[50],
                        color: a.etat === 'rupture' ? palette.danger[700] : palette.warning[700],
                      }}
                    />
                  </Box>
                ))}
            </Box>
          )}
        </CarteTableau>
      </Box>

      {graphe && storeId ? (
        <GrapheDialog
          open
          onClose={() => setGraphe(null)}
          indicateur={graphe.indicateur}
          pasInitial={graphe.pas}
          storeId={storeId}
          devise={devise}
          aujourdhui={aujourdhui}
          articlesEnAlerte={articlesEnAlerte}
          onCompleterPrixAchat={completerPrixAchat}
          coutsAbsents={coutsAbsents}
        />
      ) : null}
    </Box>
  )
}
